import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { formatMessage } from '@99designs/frontend-components/i18n';
import FormattedReactMessage from '@99designs/frontend-components/i18n/FormattedReactMessage';
import TrackScrollToPageElement from '../../../../shared/components/Analytics/TrackScrollToPageElement';
import Box from '../../../../shared/components/Box';
import Carousel from '../../../../shared/components/Carousel';
import ContentSideFeature from '../../../../shared/components/ContentSideFeature';
import Heading from '../../../../shared/components/Heading';
import { FIELD, PageField, createInlinePages } from '../../../../shared/components/InlinePage';
import Link from '../../../../shared/components/Link';
import Paragraph from '../../../../shared/components/Paragraph';
import { Currency, toUnit } from '../../../../shared/lib/currency';
import { getTitleForPackageType } from '../../../../shared/lib/getTitleForPackageType';
import localizedLowercase from '../../../../shared/lib/localizedLowercase';
import { pricingPath } from '../../../../shared/lib/platformUrls';
import { media } from '../../../../shared/lib/styles';
import { redactPersonalData } from '../../../lib/redactPersonalData';
import { BriefElementType, BriefType, CategoryType, PackageType } from '../ContestOverviewData';
import { BriefFAQs } from '../FAQs';

interface Props {
    brief: BriefType;
    category: CategoryType;
    chosenPackage: PackageType;
    deliverableFileTypes: string[];
}

const BriefWrapper = styled.div`
    height: 650px;
    max-width: 530px;

    @media ${media('small', 'medium')} {
        margin: 0 auto;
    }
`;

export default function BriefSection({
    category,
    brief,
    deliverableFileTypes,
    chosenPackage,
}: Props) {
    const intl = useIntl();

    return (
        <TrackScrollToPageElement pageName="Contest Overview" pageElement="Brief Section">
            <div className="row">
                <div className="column small-12 medium-12 normal-11 normal-offset-1 large-11 large-offset-1">
                    <ContentSideFeature
                        variations={['side-feature-right', '40-60']}
                        side={
                            <Box paddingBottom={['space14', 'space14', 'none', 'none']}>
                                <BriefWrapper>
                                    <Carousel
                                        variations={['centered-indicators']}
                                        items={createInlinePages(
                                            getPageFields(),
                                            __('The Brief'),
                                            650
                                        )}
                                        showTriggers
                                    />
                                </BriefWrapper>
                            </Box>
                        }
                        top={
                            <div className="row">
                                <div className="column small-12 medium-12 normal-10 large-10">
                                    <div className="brief-section-text-block">
                                        <Box
                                            paddingBottom={[
                                                'space5',
                                                'space5',
                                                'space14',
                                                'space14',
                                            ]}
                                            paddingTop={[
                                                'space10',
                                                'space10',
                                                'space16',
                                                'space16',
                                            ]}
                                        >
                                            <Heading
                                                type="h2"
                                                variations={['h1', 'quarter-margin', 'h1-capped']}
                                            >
                                                {__('It all began with a design brief.')}
                                            </Heading>
                                            <Paragraph>
                                                {formatMessage(
                                                    __(
                                                        'A quick, interactive guide helped them understand their design style and captured exactly what they needed in their {category}.'
                                                    ),
                                                    {
                                                        category: localizedLowercase(
                                                            intl.locale,
                                                            category.title
                                                        ),
                                                    }
                                                )}
                                            </Paragraph>
                                        </Box>
                                    </div>
                                </div>
                            </div>
                        }
                        bottom={
                            <div className="row">
                                <div className="column small-12 medium-12 normal-10 large-10">
                                    <BriefFAQs
                                        categoryAndStartingPriceLabel={categoryAndStartingPriceLabel()}
                                    />
                                </div>
                            </div>
                        }
                    />
                </div>
            </div>
        </TrackScrollToPageElement>
    );

    function getPageFields(): PageField[] {
        const fields = brief.elements
            .slice()
            .filter((field) => !isHiddenField(field.key))
            .sort((fieldA, fieldB) => fieldPriority(fieldA) - fieldPriority(fieldB))
            .reduce<PageField[]>((acc, cur) => {
                const field = toPageFieldForDisplay(cur);

                if (!field) {
                    return acc;
                }

                if (Array.isArray(field)) {
                    acc.push(...field);
                } else {
                    acc.push(field);
                }

                return acc;
            }, []);

        fields.push(getPackageField());

        if (deliverableFileTypes.length > 0) {
            fields.push(getFileDeliverablesField());
        }

        return fields;
    }

    function isHiddenField(fieldKey: string): boolean {
        const hiddenFields = ['logoBusinessName', 'businessName', 'designExamples'];
        return hiddenFields.includes(fieldKey);
    }

    function formatCurrency(price: number, currency: Currency) {
        return intl.formatNumber(toUnit(price, currency), {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
        });
    }

    function getPackageField() {
        return {
            label: getTitleForPackageType(chosenPackage.type),
            component: FIELD.InlinePageText,
            props: {
                paragraph: (
                    <>
                        <FormattedReactMessage
                            message={__(
                                'Every design category has <Link>flexible pricing</Link> for all budgets.'
                            )}
                            replacements={{
                                Link: <Link href={pricingPath()}></Link>,
                            }}
                        />
                        {` ${categoryAndStartingPriceLabel()}`}
                    </>
                ),
            },
        };
    }

    function categoryAndStartingPriceLabel(): string {
        if (!category.fromPrice) {
            return '';
        }
        return formatMessage(__('{categoryTitle} starts at {startingPrice}.'), {
            categoryTitle: category.title,
            startingPrice: formatCurrency(
                category.fromPrice.amountInCents,
                category.fromPrice.currency as Currency
            ),
        });
    }

    function getFileDeliverablesField() {
        return {
            label: __('File deliverables'),
            component: FIELD.InlinePageFileDeliverables,
            props: {
                paragraph: __(
                    'Full copyright with production-ready files for digital and/or print.'
                ),
                fileTypes: fileTypesForDisplay(deliverableFileTypes),
            },
        };
    }
}

function fieldPriority(briefElement: BriefElementType): number {
    return (
        {
            slogan: 1,
            industry: 2,
            designExamples: 3,
        }[briefElement.key] || 99
    );
}

function textFieldHasValue(element: PageField): boolean {
    return element.props.paragraph.length > 0;
}

function splitInlinePageDesignRow(element: PageField): PageField | PageField[] {
    const numDesignsPerRow = 3;
    const images = [...element.props.images];
    const inlinePageDesignRows: PageField[] = [];
    while (images.length > 0) {
        const pageField: PageField = {
            component: element.component,
            props: {
                ...element.props,
                images: images.splice(0, numDesignsPerRow),
            },
            label: element.label,
        };
        inlinePageDesignRows.push(pageField);
    }
    return inlinePageDesignRows;
}

function fileTypesForDisplay(deliverableFileTypes: string[]) {
    return Object.keys(
        deliverableFileTypes.reduce((formatedFileTypes: any, fileType) => {
            if (fileType === 'SKETCH') {
                return formatedFileTypes;
            }

            if (fileType === 'PDF-print' || fileType === 'PDF-digital') {
                formatedFileTypes['PDF'] = true;
                return formatedFileTypes;
            }

            formatedFileTypes[fileType] = true;
            return formatedFileTypes;
        }, {})
    );
}

function toPageFieldForDisplay(element: BriefElementType): PageField | PageField[] | null {
    switch (element.__typename) {
        case 'ContestOverviewTextElement': {
            const field = {
                label: getShortenedElementTitle(element),
                component: FIELD.InlinePageText,
                props: {
                    paragraph: redactPersonalData(element.textValue),
                },
            };
            return textFieldHasValue(field) ? field : null;
        }
        case 'ContestOverviewTextAreaElement': {
            const field = {
                label: getShortenedElementTitle(element),
                component: FIELD.InlinePageTextArea,
                props: {
                    paragraph: redactPersonalData(element.textAreaValue),
                },
            };
            return textFieldHasValue(field) ? field : null;
        }
        case 'ContestOverviewSlidersElement': {
            return {
                label: getShortenedElementTitle(element),
                component: FIELD.InlinePageRanges,
                props: {
                    ranges: element.sliders.map((slider) => ({
                        value: slider.value,
                        minLabel: slider.min,
                        maxLabel: slider.max,
                    })),
                },
            };
        }
        case 'ContestOverviewMultipleChoiceImageElement': {
            const urls = element.multiChoiceImgValue
                .map((val) => {
                    const choice = element.choices.find((choice) => choice.key === val);
                    return choice ? choice.url : null;
                })
                .filter((url) => url !== null);

            if (urls.length === 0) {
                return null;
            }

            return {
                label: getShortenedElementTitle(element),
                component: FIELD.InlinePageImageRow,
                props: {
                    urls: urls,
                },
            };
        }
        case 'ContestOverviewFilesElement': {
            return splitInlinePageDesignRow({
                label: getShortenedElementTitle(element),
                component: FIELD.InlinePageDesignRow,
                props: {
                    images: element.urls,
                },
            });
        }
        case 'ContestOverviewChoiceElement': {
            const choice = element.choices.find((choice) => choice.key === element.choiceValue);
            const value = choice ? choice.value : '';
            const field = {
                label: getShortenedElementTitle(element),
                component: FIELD.InlinePageText,
                props: {
                    paragraph: value,
                },
            };

            return textFieldHasValue(field) ? field : null;
        }
    }
}

function getShortenedElementTitle(element: BriefElementType): string {
    switch (element.key) {
        case 'targetAudience': {
            return __('About us');
        }
        case 'slogan': {
            return __('Slogan');
        }
        case 'industry': {
            return __('Industry');
        }
        case 'colorPreferences': {
            return __('Color inspiration');
        }
        case 'styleAttributes': {
            return __('Design style');
        }
        case 'description': {
            return __("What's your vision?");
        }
    }
    return element.title;
}
